import React, { useState, useMemo, forwardRef, useImperativeHandle } from "react";
import { useTranslation } from "react-i18next";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Button,
  Card,
  CardBody,
  Alert,
} from "reactstrap";
import Select from "components/Select";
import { useCreateReport, useUpdateReport } from "api/reports/service";
import {
  useCreateReportFolder,
  useGetReportFolders,
} from "api/report-folders/service";
import { useGetTenantUsers } from "api/users/service";
import { getActiveColorStyle } from "utils/colorUtils";
import { getTheme2SearchFilterSingleSelectStyles } from "utils/theme2MultiSelect";

const ReportDetail = forwardRef(({
  activeColor = "blue",
  isTheme2 = false,
  formData,
  setFormData,
  errors,
  setErrors,
  showValidationError,
  id,
  isEdit,
  isEditMode,
  permissions = [],
  reportType,
  selectedTenantId = null,
}, ref) => {
  const { t } = useTranslation();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: "14px" } : undefined;

  // Mutations
  const createReportMutation = useCreateReport();
  const updateReportMutation = useUpdateReport();
  const createReportFolderMutation = useCreateReportFolder();
  const CREATE_FOLDER_OPTION = "__create_new_folder__";

  const ownerId =
    formData.owner && typeof formData.owner === "object"
      ? formData.owner.id
      : formData.owner || null;
  const selectedFolderId =
    formData.report_folder && typeof formData.report_folder === "object"
      ? formData.report_folder.id || formData.report_folder.documentId
      : formData.report_folder || null;

  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");

  const { data: reportFoldersData, isLoading: isLoadingFolders } = useGetReportFolders(
    { ownerId },
    { enabled: !!ownerId && reportType !== "standard" }
  );

  // Save handler function
  const handleSave = async () => {
    try {
      const folderId =
        formData.report_folder && typeof formData.report_folder === "object"
          ? formData.report_folder.id || formData.report_folder.documentId
          : formData.report_folder || null;
      const folderName =
        formData.report_folder && typeof formData.report_folder === "object"
          ? formData.report_folder.name
          : formData.folder || "";
      const payload = {
        ...formData,
        owner: ownerId,
        report_folder: folderId || null,
        folder: folderName || "",
        permissons: permissions,
      };

      if (isEditMode) {
        await updateReportMutation.mutateAsync({
          id: id,
          data: payload,
        });
      } else {
        await createReportMutation.mutateAsync(payload);
      }
    } catch (error) {
      console.error('Error saving report detail:', error);
    }
  };

  // Expose save method to parent component
  useImperativeHandle(ref, () => ({
    handleSave
  }));

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSelectChange = (name, option) => {
    setFormData((prev) => {
      if (name === "owner") {
        const nextOwnerId = option?.value || "";
        const currentFolderOwnerId =
          prev.report_folder && typeof prev.report_folder === "object"
            ? prev.report_folder.owner_folder?.id
            : null;
        const shouldClearFolder =
          prev.report_folder &&
          currentFolderOwnerId != null &&
          String(currentFolderOwnerId) !== String(nextOwnerId);

        return {
          ...prev,
          owner: nextOwnerId,
          report_folder: shouldClearFolder ? null : prev.report_folder,
          folder: shouldClearFolder ? "" : prev.folder,
        };
      }

      if (name === "report_folder") {
        if (option?.value === CREATE_FOLDER_OPTION) {
          setIsCreatingFolder(true);
          return prev;
        }

        return {
          ...prev,
          report_folder: option
            ? {
                id: option.value,
                documentId: option.value,
                name: option.label,
              }
            : null,
          folder: option?.label || "",
        };
      }

      return {
        ...prev,
        [name]: option?.value || "",
      };
    });

    // Clear error when user selects
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Options for selects
  const primaryDataSourceOptions = [
    { value: "", label: t('reports.selectPrimaryDataSource') },
    { value: "inventory", label: "Inventory" },
    { value: "deal", label: "Deal / Lead" },
    { value: "customers", label: "Customers" },
    { value: "accounts", label: "Accounts" },
  ];

  const { data: usersData } = useGetTenantUsers(1, 100);

  const ownerOptions = useMemo(() => {
    return usersData?.results?.map(user => ({
      value: user.id,
      label: user?.user_detail?.full_name || user?.full_name || user?.email || user?.username
    })) || [];
  }, [usersData]);

  const folderOptions = useMemo(
    () => [
      {
        value: CREATE_FOLDER_OPTION,
        label: t("reports.createFolder"),
      },
      ...((reportFoldersData?.results || []).map((folder) => ({
        value: folder.documentId || folder.id,
        label: folder.name,
      }))),
    ],
    [CREATE_FOLDER_OPTION, reportFoldersData, t]
  );

  const handleCreateFolder = async () => {
    const trimmedName = String(newFolderName || "").trim();
    if (!ownerId || !trimmedName) return;

    try {
      const createdFolder = await createReportFolderMutation.mutateAsync({
        name: trimmedName,
        reportId: id || undefined,
        reportOwnerId: ownerId || undefined,
      });

      const nextId = createdFolder?.documentId || createdFolder?.id;
      setFormData((prev) => ({
        ...prev,
        report_folder: nextId
          ? {
              id: nextId,
              documentId: nextId,
              name: createdFolder?.name || trimmedName,
              owner_folder: createdFolder?.owner_folder || { id: ownerId },
            }
          : prev.report_folder,
        folder: createdFolder?.name || trimmedName,
      }));
      setNewFolderName("");
      setIsCreatingFolder(false);
    } catch (error) {
      console.error("Error creating report folder:", error);
    }
  };


  const summaryReportOptions = [
    { value: false, label: t('common.no') },
    { value: true, label: t('common.yes') },
  ];

  return (
    <Card>
      {showValidationError && (
        <Alert color="danger" className="mb-4" style={{ fontWeight: 600 }}>
          {t('reports.validationError')}
        </Alert>
      )}
      {(isEdit || !isEditMode) && (
        <CardBody>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="name">{t('reports.reportName')} <span style={{ color: 'red' }}>*</span></Label>
                <Input
                  type="text"
                  name="name"
                  id="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  style={inputStyle}
                  placeholder={t('reports.enterReportName')}
                />
                {errors.name && (
                  <div className="invalid-feedback d-block">
                    {errors.name}
                  </div>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup>
                <Label for="description">{t('reports.reportDesc')}</Label>
                <Input
                  type="text"
                  name="description"
                  id="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  className="form-control"
                  style={inputStyle}
                  placeholder={t('reports.enterReportDesc')}
                />
              </FormGroup>
            </Col>
          </Row>

          <Row>
            <Col md="6">
              <FormGroup>
                <Label for="primary_data_source">{t('reports.primaryDataSource')} <span style={{ color: 'red' }}>*</span></Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('reports.selectPrimaryDataSource')}
                  name="primary_data_source"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={formData.primary_data_source ? {
                    value: formData.primary_data_source,
                    label: primaryDataSourceOptions.find(opt => opt.value === formData.primary_data_source)?.label || formData.primary_data_source
                  } : null}
                  onChange={(option) => handleSelectChange('primary_data_source', option)}
                  options={primaryDataSourceOptions}
                  isDisabled={isEditMode}
                />
                {errors.primary_data_source && (
                  <div className="invalid-feedback d-block">
                    {errors.primary_data_source}
                  </div>
                )}
              </FormGroup>
            </Col>
            {reportType !== 'standard' && (
              <Col md="6">
                <FormGroup>
                  <Label for="owner">{t('reports.owner')}</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder={t('reports.selectOwner')}
                    name="owner"
                    isSearchable={true}
                    closeMenuOnSelect={true}
                    value={formData.owner ? {
                      value: (formData.owner && typeof formData.owner === 'object') ? formData.owner.id : formData.owner,
                      label: (formData.owner && typeof formData.owner === 'object')
                        ? (formData.owner?.user_detail?.full_name || formData.owner?.full_name || formData.owner?.username)
                        : (ownerOptions.find(opt => opt.value === formData.owner)?.label || formData.owner)
                    } : null}
                    onChange={(option) => handleSelectChange('owner', option)}
                    options={ownerOptions}
                  />
                </FormGroup>
              </Col>
            )}
          </Row>

          <Row>
            {reportType !== 'standard' && (
              <Col md="6">
                <FormGroup>
                  <Label for="report_folder">{t('reports.folder')}</Label>
                  <Select
                    activeColor={activeColor}
                    styles={selectStyles}
                    className="react-select"
                    placeholder={
                      ownerId
                        ? (isLoadingFolders ? t("common.loading") : t("reports.selectFolder"))
                        : t("reports.selectOwnerFirst")
                    }
                    name="report_folder"
                    isSearchable
                    closeMenuOnSelect={true}
                    value={
                      selectedFolderId
                        ? {
                            value: selectedFolderId,
                            label:
                              folderOptions.find((opt) => String(opt.value) === String(selectedFolderId))?.label ||
                              formData.report_folder?.name ||
                              formData.folder,
                          }
                        : null
                    }
                    onChange={(option) => handleSelectChange("report_folder", option)}
                    options={folderOptions}
                    isClearable
                    isDisabled={!ownerId || isLoadingFolders}
                  />
                  {!ownerId ? (
                    <small className="text-muted d-block mt-2">
                      {t("reports.selectOwnerFirst")}
                    </small>
                  ) : null}
                  {isCreatingFolder ? (
                    <div className="mt-2">
                      <Input
                        type="text"
                        value={newFolderName}
                        onChange={(e) => setNewFolderName(e.target.value)}
                        placeholder={t("reports.enterFolderName")}
                        className="form-control"
                        style={inputStyle}
                      />
                      <div className="d-flex align-items-center mt-2" style={{ gap: 8 }}>
                        <Button
                          type="button"
                          size="sm"
                          style={activeColorStyle}
                          onClick={handleCreateFolder}
                          disabled={
                            !ownerId ||
                            !newFolderName.trim() ||
                            createReportFolderMutation.isPending
                          }
                        >
                          {createReportFolderMutation.isPending
                            ? t("common.saving")
                            : t("common.save")}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          color="secondary"
                          onClick={() => {
                            setIsCreatingFolder(false);
                            setNewFolderName("");
                          }}
                          disabled={createReportFolderMutation.isPending}
                        >
                          {t("common.cancel")}
                        </Button>
                      </div>
                    </div>
                  ) : null}
                </FormGroup>
              </Col>
            )}
            <Col md="6">
              <FormGroup>
                <Label for="summary_report">{t('reports.summaryReport')}</Label>
                <Select
                  activeColor={activeColor}
                  styles={selectStyles}
                  className="react-select"
                  placeholder={t('reports.selectSummaryReport')}
                  name="summary_report"
                  isSearchable={false}
                  closeMenuOnSelect={true}
                  value={formData.summary_report !== undefined ? {
                    value: formData.summary_report,
                    label: formData.summary_report ? t('common.yes') : t('common.no')
                  } : null}
                  onChange={(option) => handleSelectChange('summary_report', option)}
                  options={summaryReportOptions}
                />
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
      )}

    </Card>
  );
});

ReportDetail.displayName = 'ReportDetail';

export default ReportDetail;
